import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Button as PrimeButton, ButtonProps as PrimeButtonProps } from 'primereact/button';
import { forwardRef, ReactNode } from 'react';

import type { clientTypes } from '@bedc/client/types';
import { c } from '@bedc/client/utils';

import { Icon } from '../misc/Icon';

export type ButtonProps = Omit<
	PrimeButtonProps,
	'badge' | 'icon' | 'label' | 'severity' | 'size'
> & {
	/** The icon to show in the button. */
	icon?: IconDefinition | null;

	/** The text label of the button. */
	label?: ReactNode;

	/** The right icon to show in the button. */
	rightIcon?: IconDefinition | null;

	/** Alternative size of the button. */
	size?: Extract<clientTypes.ui.Size, 'xs' | 'sm' | 'md' | 'lg'>;

	/** Whether the button should stay highlighted. */
	checked?: boolean;
};

export const Button = forwardRef<PrimeButton, ButtonProps>(
	(
		{
			icon,
			label,
			rightIcon,
			size = 'md',
			checked,
			// Always specify default type as browsers can use different defaults.
			// @see https://www.w3schools.com/tags/att_button_type.asp
			type = 'button',

			...buttonProps
		},
		ref
	) => {
		// Template
		// ------------------------------------------------------------------------

		return (
			<PrimeButton
				ref={ref}
				{...buttonProps}
				className={c(
					size === 'xs' && `p-button-${size}`,
					checked && 'p-button-highlight',
					buttonProps.text && 'data-pressed:bg-accent-200!',
					icon && !label && 'p-button-icon-only',
					buttonProps.className
				)}
				aria-valuetext={checked ? 'true' : 'false'}
				type={type}
				icon={
					icon ? (
						<Icon
							className="p-button-icon p-button-icon-left"
							icon={icon}
							fixedWidth={!label}
						/>
					) : null
				}
				size={size === 'sm' ? 'small' : size === 'lg' ? 'large' : undefined}
				// ! Label actually can have an element, error in PrimeReact TS definitions.
				// @ts-expect-error ...
				label={
					<>
						{label}
						{rightIcon && (buttonProps.iconPos !== 'right' || !buttonProps.loading) ? (
							<Icon
								className="p-button-icon p-button-icon-right pointer-events-none"
								icon={rightIcon}
								fixedWidth={!label}
							/>
						) : null}
					</>
				}
				tooltipOptions={{
					position: 'bottom',
					showDelay: 250,
					...buttonProps.tooltipOptions,
				}}
			/>
		);
	}
);
